'use client';

import type { CMSProductCard } from '@/lib/cms/types';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import styles from './BuckleSelector.module.scss';

export interface BuckleSelectorProps {
  /** Available buckle products compatible with this strap */
  buckles: CMSProductCard[];
  /** Currently selected buckle (by SKU) */
  selectedBuckleSku: string | null;
  /** Callback when a buckle is selected */
  onBuckleSelect: (buckle: CMSProductCard) => void;
  /** Whether the strap has an integrated buckle (no selection needed) */
  hasIntegratedBuckle?: boolean;
  /** Title to display above the selector */
  title?: string;
  /** Class name for container */
  className?: string;
}

/**
 * Buckle selector for strap PDPs
 * Shows compatible buckles as a grid of selectable product cards
 */
export function BuckleSelector({
  buckles,
  selectedBuckleSku,
  onBuckleSelect,
  hasIntegratedBuckle = false,
  title,
  className,
}: BuckleSelectorProps) {
  const t = useTranslations('product.strap');

  // Integrated buckle - no selection needed
  if (hasIntegratedBuckle) {
    return (
      <div className={clsx(styles.container, styles.integrated, className)}>
        <div className={styles.integratedInfo}>
          <span className={styles.integratedLabel}>{t('integratedBuckle')}</span>
          <span className={styles.integratedNote}>{t('integratedBuckleNote')}</span>
        </div>
      </div>
    );
  }

  // No compatible buckles
  if (buckles.length === 0) {
    return null;
  }

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.header}>
        <span className={styles.title}>{title || t('selectBuckle')}</span>
        <span className={styles.subtitle}>{t('buckleSubtitle')}</span>
      </div>

      <div className={styles.grid}>
        {buckles.map((buckle) => {
          const isSelected = selectedBuckleSku === buckle.sku;
          const imageUrl = buckle.image?.url;
          // CTAs like 'NO_CTA' or 'NOTIFY' mean unavailable for purchase
          const isAvailable =
            !buckle.ctaType || buckle.ctaType === 'ORDER' || buckle.ctaType === 'PRE_ORDER';

          return (
            <button
              key={buckle.sku || buckle.sys.id}
              type="button"
              className={clsx(styles.buckleCard, {
                [styles.selected]: isSelected,
                [styles.unavailable]: !isAvailable,
              })}
              disabled={!isAvailable}
              onClick={() => onBuckleSelect(buckle)}
              aria-pressed={isSelected}
              aria-label={`${buckle.name} - ${buckle.formattedPrice || ''}${!isAvailable ? ` - ${t('unavailable')}` : ''}`}
            >
              <div className={styles.imageWrapper}>
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={buckle.name}
                    fill
                    sizes="(max-width: 768px) 25vw, 120px"
                    className={styles.image}
                  />
                ) : (
                  <div className={styles.placeholderImage}>
                    <span className={styles.placeholderText}>{buckle.name.charAt(0)}</span>
                  </div>
                )}
                {isSelected && (
                  <div className={styles.selectedBadge}>
                    <span className={styles.checkmark}>✓</span>
                  </div>
                )}
              </div>

              <div className={styles.info}>
                <span className={styles.buckleName}>{buckle.name}</span>
                {buckle.material && (
                  <span className={styles.buckleMaterial}>{buckle.material}</span>
                )}
                {buckle.formattedPrice && (
                  <span className={styles.bucklePrice}>{buckle.formattedPrice}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
